import { createContext } from 'react';
import type { Id, ToastContent } from 'react-toastify/dist/types';

import type { GdpDtoItem } from '@components/gdpSelect';

import {
  DEFAULT_FILTERS,
  type DefaultFiltersType,
} from '@hooks/useDefaultFilters';

import type { AclBuilder } from '@system/acl/aclBuilder';
import type { NotificationState } from '@system/app/store/notification.store';
import { defaultNotificationState } from '@system/app/store/notification.store';

import type { CallbackEvent } from '@utils/eventBus/useEventBus';

import type { NotificationEvents } from './constants';
import type { ConfirmDialogState } from './store/confirmDialog.store';
import type { EnvIds } from './store/envIds.store';
import { $envIds } from './store/envIds.store';
import type { HeaderMenuItem } from './store/mainMenu.store';
import type {
  ContextToastOptions,
  FeatureFlagsDto,
  MeDto,
  NotificationEventHandler,
} from './types';

export type AppContextValue = {
  refetchInitData: () => void;
  confirmDialog: {
    set: (dialog: ConfirmDialogState | null) => void;
    use: () => ConfirmDialogState | null;
  };
  me?: MeDto;
  acl: AclBuilder;
  envIds: EnvIds;
  mainMenu: {
    set: (menu: HeaderMenuItem[]) => void;
    clear: () => void;
    use: () => HeaderMenuItem[];
  };
  flags?: FeatureFlagsDto;
  gdp: {
    current: GdpDtoItem | null;
    next: GdpDtoItem | null;
  };
  toast: {
    info: <TData = unknown>(
      content: ToastContent<TData>,
      options?: ContextToastOptions,
    ) => Id;
    error: <TData = unknown>(
      content: ToastContent<TData>,
      options?: ContextToastOptions,
    ) => Id;
    success: <TData = unknown>(
      content: ToastContent<TData>,
      options?: ContextToastOptions,
    ) => Id;
    dismiss: (id?: Id | undefined) => void;
  };
  notification: {
    publish: <T = unknown>(eventName: string, payload?: T) => void;
    subscribe: <T>(
      event: NotificationEvents,
      handler: NotificationEventHandler<T>,
    ) => CallbackEvent;
    unsubscribe: (event: NotificationEvents, handler: CallbackEvent) => void;
  };
  notificationUI: {
    toggle: () => void;
    setCount: (count: number) => void;
    use: () => NotificationState;
  };
  isAdm: boolean;
  defaultFilters: DefaultFiltersType;
  isReadOnlyWorker: boolean;
};

const CONTEXT_DEFAULT_VALUE: AppContextValue = {
  refetchInitData: () => {},
  confirmDialog: {
    use: () => null,
    set: () => {},
  },
  me: {} as unknown as MeDto,
  envIds: $envIds.getState(),
  acl: {
    can(): boolean {
      return false;
    },
  } as unknown as AclBuilder,
  mainMenu: {
    set: () => {},
    clear: () => {},
    use: () => [],
  },
  flags: [],
  gdp: {
    current: null,
    next: null,
  },
  toast: {
    info: () => '',
    error: () => '',
    success: () => '',
    dismiss: () => '',
  },
  notification: {
    subscribe: () => () => {},
    unsubscribe: () => {},
    publish: () => {},
  },
  notificationUI: {
    toggle: () => {},
    setCount: () => {},
    use: () => defaultNotificationState,
  },
  isAdm: false,
  defaultFilters: DEFAULT_FILTERS,
  isReadOnlyWorker: false,
};
export const AppContext = createContext<AppContextValue>(CONTEXT_DEFAULT_VALUE);
